// ResponsibilityAssignmentList — the read/summary list of a scope's
// responsibilities + their chains, for a peek body or a detail section. Reusable
// across every scope that carries responsibilities (zone · cluster · team ·
// vehicle-pool · site · warehouse).
//
// This is a DISTINCT pattern from ResponsibilityChainEditor: the editor WRITES
// one responsibility's lead→backup chain (a drawer); this READS the whole set at
// a scope (each row = ResponsibilityAssignmentRow). The people on a row are
// CONTENT the host may render as references (`renderMember` — an `EntityRef`
// that peeks the person); a row is a door only inside the chains editor's own
// list (`onManage` — pick a chain to edit), never on an entity's card, whose
// one way in is its own act (architect-ruled at the team's live look). The
// coverage header ("N people · K required missing") is composed by the host
// from the shared ResponsibilityCoverage — kept separate so a detail section
// (which already has a title) and a peek (inline title) compose it their own
// way.
//
// Empty state: when the level has NO responsibility definitions, it shows a quiet
// "none defined" message rather than an empty box (the host supplies the copy,
// which may link to the catalogue where they're defined).

import type { ReactNode } from 'react';
import {
  ResponsibilityAssignmentRow,
  type ResponsibilityAssignmentMember,
  type ResponsibilityAssignmentRowLabels,
} from './ResponsibilityAssignmentRow.js';

export interface ResponsibilityAssignmentItem {
  /** Responsibility id (the chain key for the editor's onManage). */
  id: string;
  name: string;
  required: boolean;
  /** Assignees in chain order (position 1 = lead); empty = unassigned. */
  members: ResponsibilityAssignmentMember[];
}

export interface ResponsibilityAssignmentListProps {
  items: ResponsibilityAssignmentItem[];
  /** A person as the host renders them (a reference to the person); default: the name as text. */
  renderMember?: (member: ResponsibilityAssignmentMember) => ReactNode;
  /** The chains EDITOR's list only: each row opens that chain's stage. Never on an entity's card. */
  onManage?: (id: string) => void;
  /** Shown when the level has no responsibility definitions (string or richer node). */
  empty: ReactNode;
  rowLabels: ResponsibilityAssignmentRowLabels;
  className?: string;
}

export function ResponsibilityAssignmentList({
  items,
  renderMember,
  onManage,
  empty,
  rowLabels,
  className = '',
}: ResponsibilityAssignmentListProps) {
  if (items.length === 0) {
    return (
      <p
        className={`rounded-md border border-dashed border-[var(--color-border)] px-3 py-6 text-center text-sm text-[var(--color-text-secondary)] ${className}`}
      >
        {empty}
      </p>
    );
  }
  return (
    <ul className={`flex flex-col gap-2 ${className}`}>
      {items.map((it) => (
        <li key={it.id}>
          <ResponsibilityAssignmentRow
            name={it.name}
            required={it.required}
            members={it.members}
            renderMember={renderMember}
            onManage={onManage ? () => onManage(it.id) : undefined}
            labels={rowLabels}
          />
        </li>
      ))}
    </ul>
  );
}
